import { z } from 'zod';

/**
 * The org contact book (wayai#4931 D6): an org's contacts and its org lists, org data rather than
 * hub config, held by one `ContactBookDO` per org (`contacts:{org_id}`). Out of `hub.yaml` and out
 * of branching; managed through `/api/contact-book`, CSV import and the org settings Contacts tab.
 *
 * WHO (owner decision 1). An org's contact-book editors are the callers whose grants hold
 * `org:admin` or `contacts:write` over every hub of the org, every hub tag and both environments
 * (`isContactBookEditor` in `auth/grants.ts`). They read and write every contact and org list,
 * save a contact's identity (IDENTITY). A hub admin reads, through `/api/contact-book/hubs/:hubId/contacts`, only the contacts that hub
 * sees (VISIBILITY), and reads no org list's members.
 *
 * VISIBILITY (D7, D9). A hub sees a contact iff `checkPolicyMatch(contact.scope_tag_ids,
 * contact.environment, hub.tags, hub.environment)` passes — the rule for org credentials and
 * resources: a contact with no scope tag is seen by every hub, one with scope tags by a hub that
 * carries one of them, and only in its `environment` (`production` by default, so a preview hub
 * sees only `preview` and `all` contacts). The hub's tags and environment are read from its HubDO,
 * never from the org's hub registry (owner decision 17). Creating or importing a contact takes an
 * explicit `visibility` — scope tags, or `all_hubs` stated outright (owner decision 3) — so no
 * contact is seen by every hub by omission.
 *
 * ORG LISTS (D6a). A list is a filter over the contacts' labels and scope tags, plus pinned
 * contacts. A contact is a member when it is pinned, or when the filter is not empty and the
 * contact carries one of its labels (when it names labels) and one of its scope tags (when it
 * names scope tags); a list with an empty filter holds its pins only. An automation names one by
 * `target: { type: contact_list, org_list: <name> }`, and each fire reaches the list's members
 * that its own hub sees. Deleting a list an automation names is allowed: the fire records
 * `list_not_found` and raises the hub's alert.
 *
 * LINKS (D10, owner decision 4). A contact may link to a WayAI account that holds a hub user in
 * one of the org's hubs, chosen by an editor who holds the IDENTITY bar:
 * `GET /link-candidates` answers, for an email, only the accounts whose hub user in one of the
 * org's hubs carries it, so linking reveals nothing about an arbitrary address.
 *
 * IDENTITY (#5489, owner decision D18 on #5040). On an existing contact a new `phone` or
 * `instagram_sid` and any account link take the identity bar: a person signed in interactively who
 * is an editor and may write the org's data — never a token. An editor without it is refused (403).
 * Clearing one, a create and an import stay every editor's.
 *
 * PERSON LINKS AND THE DENY LIST (wayai-pro/wayai#5473 §1) are `person-links.ts`'s, on the same mount.
 *
 * SUPPRESSIONS (D8, owner decision 9). The book holds who asked not to be sent automations: at
 * most one suppression per channel identity — a phone (E.164), an email's mailbox (lowercased,
 * without a `+` subaddress, and for Gmail without its dots) or an Instagram id — stored as a keyed
 * hash under a random salt the book holds, never rotated and destroyed with the org's book. So a
 * suppression names no one and outlives the deletion or erasure of any contact holding its
 * identity. Its scope is `all` or `marketing`: a `marketing` automation's send is stopped by
 * either, an `operational` one's only by `all` (`AUTOMATION_PURPOSES`). A write only ever widens a
 * suppression (`marketing` to `all`); an editor narrows one by removing it and adding it again.
 * An automation's fire checks the identity it sends to — the phone on WhatsApp, the email on email,
 * the Instagram id on Instagram, and every identity the contact holds on the app — and an ordinary
 * agent reply is never checked. Writers: an editor, with either scope (`POST /suppressions`); the
 * one-click email unsubscribe, with `all` (`CONTACT_BOOK_EMAIL_UNSUBSCRIBE_PATH`, no sign-in), whose
 * link every marketing automation email carries; and Meta's marketing opt-out, with `marketing`:
 * a WhatsApp marketing message refused because its recipient stopped marketing messages from the
 * business holds that phone.
 */

// ---------------------------------------------------------------------------
// Vocabulary and caps
// ---------------------------------------------------------------------------

/** Where a contact is seen (D9): hubs of that environment, or of both for `all`. */
export const CONTACT_ENVIRONMENTS = ['production', 'preview', 'all'] as const;
export type ContactEnvironment = typeof CONTACT_ENVIRONMENTS[number];

export const CONTACT_NAME_MAX = 200;
export const CONTACT_EMAIL_MAX = 320;
/** The longest phone input; it is stored normalized (`normalizeContactPhone`). */
export const CONTACT_PHONE_INPUT_MAX = 40;
export const CONTACT_INSTAGRAM_SID_MAX = 64;
/** The serialized size of a contact's `metadata` object. */
export const CONTACT_METADATA_MAX_BYTES = 4096;
export const CONTACT_LABEL_MAX = 64;
export const CONTACT_LABELS_MAX = 20;
export const CONTACT_SCOPE_TAGS_MAX = 20;
/** The most contacts one org's book holds. */
export const CONTACT_BOOK_CONTACTS_MAX = 100_000;
/** The most contacts one page of a contact list read carries. */
export const CONTACT_PAGE_MAX = 100;
/** The most rows one import request carries; a CSV file is sent in batches of at most this. */
export const CONTACT_IMPORT_BATCH_MAX = 500;
export const CONTACT_SEARCH_MAX = 100;

export const CONTACT_LISTS_MAX = 200;
export const CONTACT_LIST_NAME_MAX = 64;
/** An org list's name: a slug, the reference automations and `hub.yaml` carry. */
export const CONTACT_LIST_NAME_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const CONTACT_LIST_DESCRIPTION_MAX = 1000;
/** The most labels, and the most scope tags, one list filter names. */
export const CONTACT_LIST_FILTER_MAX = 20;
export const CONTACT_LIST_PINS_MAX = 1000;
/** The most pins one pins request adds or removes. */
export const CONTACT_LIST_PINS_PER_REQUEST_MAX = 100;

/** The most link candidates one email lookup answers. */
export const CONTACT_LINK_CANDIDATES_MAX = 20;

/** What a suppression stops: every automation send (`all`), or `marketing` ones only. */
export const SUPPRESSION_SCOPES = ['all', 'marketing'] as const;
export type SuppressionScope = typeof SUPPRESSION_SCOPES[number];

/** The channel identity a suppression is held against. */
export const SUPPRESSION_IDENTITY_KINDS = ['phone', 'email', 'instagram'] as const;
export type SuppressionIdentityKind = typeof SUPPRESSION_IDENTITY_KINDS[number];

/**
 * Who wrote a suppression's scope: an editor, the person through an email's unsubscribe link, or
 * the person through WhatsApp, which refused a marketing message because they stopped marketing
 * messages from the business (Meta's marketing opt-out).
 */
export const SUPPRESSION_SOURCES = ['admin', 'unsubscribe', 'whatsapp_opt_out'] as const;
export type SuppressionSource = typeof SUPPRESSION_SOURCES[number];

/** A suppression's id: its identity's keyed hash, hex. */
export const SUPPRESSION_ID_REGEX = /^[0-9a-f]{64}$/;

// ---------------------------------------------------------------------------
// Normalization (shared by the server, which stores what these return, and the web import)
// ---------------------------------------------------------------------------

/**
 * A phone as the book stores it: E.164, `+` and 8 to 15 digits. Spaces, dashes, dots and
 * parentheses are dropped and a leading `00` reads as `+`; anything else, or a number without
 * its country code, answers null.
 */
export function normalizeContactPhone(raw: string): string | null {
  const compact = raw.trim().replace(/[\s\-.()]/g, '');
  const withPlus = compact.startsWith('00') ? `+${compact.slice(2)}` : compact;
  return /^\+[1-9]\d{7,14}$/.test(withPlus) ? withPlus : null;
}

/** A label as the book stores it: trimmed, inner whitespace collapsed, lowercased. */
export function normalizeContactLabel(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ').toLowerCase();
}

// ---------------------------------------------------------------------------
// Field schemas
// ---------------------------------------------------------------------------

const contactName = z.string().trim().min(1).max(CONTACT_NAME_MAX);

const contactPhone = z.string().max(CONTACT_PHONE_INPUT_MAX).transform((raw, ctx) => {
  const phone = normalizeContactPhone(raw);
  if (phone === null) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Phone must be an international number, e.g. +5511999999999' });
    return z.NEVER;
  }
  return phone;
});

const contactEmail = z.string().trim().toLowerCase().max(CONTACT_EMAIL_MAX).email();

const contactInstagramSid = z.string().trim().min(1).max(CONTACT_INSTAGRAM_SID_MAX);

/** A stored label: never empty, and never holding a comma, which separates labels in the web's forms. */
const contactLabel = z.string().max(CONTACT_LABEL_MAX).transform(normalizeContactLabel)
  .refine((label) => label.length > 0, 'A label cannot be empty')
  .refine((label) => !label.includes(','), 'A label cannot contain a comma');

const contactLabels = z.array(contactLabel).max(CONTACT_LABELS_MAX)
  .transform((labels) => [...new Set(labels)]);

const contactMetadata = z.record(z.unknown()).refine(
  (metadata) => new TextEncoder().encode(JSON.stringify(metadata)).length <= CONTACT_METADATA_MAX_BYTES,
  `Metadata must serialize to at most ${CONTACT_METADATA_MAX_BYTES} bytes`,
);

const scopeTagIds = z.array(z.string().uuid()).min(1).max(CONTACT_SCOPE_TAGS_MAX)
  .transform((ids) => [...new Set(ids)]);

/**
 * The visibility a create or an import states (owner decision 3): `all_hubs`, said outright, or
 * the scope tags a hub must carry one of to see the contact. There is no default.
 */
export const contactVisibilitySchema = z.discriminatedUnion('scope', [
  z.object({ scope: z.literal('all_hubs') }).strict(),
  z.object({ scope: z.literal('tags'), tag_ids: scopeTagIds }).strict(),
]);
export type ContactVisibility = z.infer<typeof contactVisibilitySchema>;

/** The scope tags a visibility stores: none for `all_hubs`. */
export function scopeTagIdsOf(visibility: ContactVisibility): string[] {
  return visibility.scope === 'tags' ? visibility.tag_ids : [];
}

const hasIdentity = (row: { phone?: string | null; email?: string | null; instagram_sid?: string | null }) =>
  Boolean(row.phone || row.email || row.instagram_sid);
const IDENTITY_REQUIRED = 'A contact needs a phone, an email or an Instagram id';

// ---------------------------------------------------------------------------
// Params & queries
// ---------------------------------------------------------------------------

export const contactBookOrgQuery = z.object({
  organization_id: z.string().uuid(),
});

export const contactBookContactParam = z.object({
  contactId: z.string().uuid(),
});

export const contactBookListParam = z.object({
  listId: z.string().uuid(),
});

export const contactBookHubParam = z.object({
  hubId: z.string().uuid(),
});

/**
 * One page of contacts: `cursor` is the previous page's `next_cursor` — the last row's name and
 * id, so its length follows `CONTACT_NAME_MAX` (four bytes a character at most, base64url).
 */
export const contactBookPageQuery = z.object({
  cursor: z.string().max(2048).optional(),
  limit: z.coerce.number().int().min(1).max(CONTACT_PAGE_MAX).default(50),
});

/** The org view's contacts: a page, narrowed by a search and by a label or a scope tag. */
export const listContactBookContactsQuery = contactBookOrgQuery.merge(contactBookPageQuery).extend({
  q: z.string().trim().max(CONTACT_SEARCH_MAX).optional(),
  label: z.string().max(CONTACT_LABEL_MAX).transform(normalizeContactLabel).optional(),
  scope_tag_id: z.string().uuid().optional(),
});

/** A hub admin's view: a page of the contacts the hub sees, narrowed by a search. */
export const listHubContactBookContactsQuery = contactBookPageQuery.extend({
  q: z.string().trim().max(CONTACT_SEARCH_MAX).optional(),
});

/** The accounts an email names among the org's hub users (owner decision 4). */
export const contactBookLinkCandidatesQuery = contactBookOrgQuery.extend({
  email: contactEmail,
});

export const listContactBookListMembersQuery = contactBookOrgQuery.merge(contactBookPageQuery);

// ---------------------------------------------------------------------------
// Bodies
// ---------------------------------------------------------------------------

/** A contact's fields, as a create or an import row states them. */
const contactRowFields = {
  name: contactName,
  phone: contactPhone.nullish(),
  email: contactEmail.nullish(),
  instagram_sid: contactInstagramSid.nullish(),
  labels: contactLabels.optional(),
  metadata: contactMetadata.optional(),
};

/** Create one contact. `visibility` is required (owner decision 3); `environment` defaults to production (D9). */
export const createContactBookContactBody = z.object({
  organization_id: z.string().uuid(),
  ...contactRowFields,
  visibility: contactVisibilitySchema,
  environment: z.enum(CONTACT_ENVIRONMENTS).optional(),
}).strict().refine(hasIdentity, IDENTITY_REQUIRED);

/**
 * Update a contact: each field present replaces the stored one. A null `phone`, `email` or
 * `instagram_sid` clears it, but a contact always keeps one of them. `visibility` restates the
 * scope (an empty one is `all_hubs`, said outright again).
 */
export const updateContactBookContactBody = z.object({
  name: contactName.optional(),
  phone: contactPhone.nullable().optional(),
  email: contactEmail.nullable().optional(),
  instagram_sid: contactInstagramSid.nullable().optional(),
  labels: contactLabels.optional(),
  metadata: contactMetadata.optional(),
  visibility: contactVisibilitySchema.optional(),
  environment: z.enum(CONTACT_ENVIRONMENTS).optional(),
}).strict();

/** One import row: a contact's fields; the batch states its visibility and environment. */
export const contactImportRowSchema = z.object(contactRowFields).strict().refine(hasIdentity, IDENTITY_REQUIRED);
export type ContactImportRow = z.infer<typeof contactImportRowSchema>;

/**
 * One import batch (owner decision 3): at most `CONTACT_IMPORT_BATCH_MAX` rows, each stored with
 * the batch's `visibility` — required, never defaulted — its `environment`, and its `labels`
 * beside the row's own. A row whose phone, email or Instagram id another contact of the book (or
 * an earlier row of the batch) already holds is not stored and is answered `duplicate`.
 */
export const importContactBookContactsBody = z.object({
  organization_id: z.string().uuid(),
  visibility: contactVisibilitySchema,
  environment: z.enum(CONTACT_ENVIRONMENTS).optional(),
  labels: contactLabels.optional(),
  rows: z.array(contactImportRowSchema).min(1).max(CONTACT_IMPORT_BATCH_MAX),
}).strict().superRefine((batch, ctx) => {
  // A row is stored with its own labels and the batch's: together they keep the contact's bound,
  // so the stored contact is one an edit can save again.
  batch.rows.forEach((row, index) => {
    if (importRowLabels(row, batch.labels).length > CONTACT_LABELS_MAX) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['rows', index, 'labels'],
        message: `A contact holds at most ${CONTACT_LABELS_MAX} labels, the batch's included`,
      });
    }
  });
});

/** The labels an import row is stored with: its own and the batch's, each once. */
export function importRowLabels(row: Pick<ContactImportRow, 'labels'>, batchLabels: readonly string[] | undefined): string[] {
  return [...new Set([...(row.labels ?? []), ...(batchLabels ?? [])])];
}

/** Link a contact to an account (D10): one of the candidates `GET /link-candidates` answered. */
export const linkContactBookContactBody = z.object({
  user_id: z.string().min(1).max(128),
}).strict();

const listName = z.string().trim().min(1).max(CONTACT_LIST_NAME_MAX).regex(CONTACT_LIST_NAME_REGEX, {
  message: 'Must be lowercase alphanumeric with hyphens (e.g. "vip-customers")',
});

/** An org list's filter: labels and scope tags (`CONTACT_LIST_FILTER_MAX` each). */
export const contactListFilterSchema = z.object({
  labels: z.array(contactLabel).max(CONTACT_LIST_FILTER_MAX).transform((l) => [...new Set(l)]).default([]),
  scope_tag_ids: z.array(z.string().uuid()).max(CONTACT_LIST_FILTER_MAX).transform((t) => [...new Set(t)]).default([]),
}).strict();
export type ContactListFilter = z.infer<typeof contactListFilterSchema>;

export const createContactBookListBody = z.object({
  organization_id: z.string().uuid(),
  name: listName,
  description: z.string().max(CONTACT_LIST_DESCRIPTION_MAX).nullish(),
  filter: contactListFilterSchema.optional(),
}).strict();

/** Update an org list. Renaming it breaks every automation that names the old name (its fire records `list_not_found`). */
export const updateContactBookListBody = z.object({
  name: listName.optional(),
  description: z.string().max(CONTACT_LIST_DESCRIPTION_MAX).nullable().optional(),
  filter: contactListFilterSchema.optional(),
}).strict();

export const updateContactBookListPinsBody = z.object({
  add: z.array(z.string().uuid()).max(CONTACT_LIST_PINS_PER_REQUEST_MAX).default([]),
  remove: z.array(z.string().uuid()).max(CONTACT_LIST_PINS_PER_REQUEST_MAX).default([]),
}).strict().refine((b) => b.add.length + b.remove.length > 0, 'Add or remove at least one contact');

// ---------------------------------------------------------------------------
// Suppressions (D8)
// ---------------------------------------------------------------------------

/** A channel identity, normalized as a contact's is: the phone E.164, the email lowercased. */
export const suppressionIdentitySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('phone'), value: contactPhone }).strict(),
  z.object({ kind: z.literal('email'), value: contactEmail }).strict(),
  z.object({ kind: z.literal('instagram'), value: contactInstagramSid }).strict(),
]);
export type SuppressionIdentity = z.infer<typeof suppressionIdentitySchema>;

/** Suppress an identity. A suppression it already holds is only ever widened (`marketing` to `all`). */
export const createContactBookSuppressionBody = z.object({
  organization_id: z.string().uuid(),
  identity: suppressionIdentitySchema,
  scope: z.enum(SUPPRESSION_SCOPES),
}).strict();

export const contactBookSuppressionParam = z.object({
  suppressionId: z.string().regex(SUPPRESSION_ID_REGEX),
});

/** A page of the book's suppressions, newest first. */
export const listContactBookSuppressionsQuery = contactBookOrgQuery.merge(contactBookPageQuery);

// The one-click unsubscribe route's path, `CONTACT_BOOK_EMAIL_UNSUBSCRIBE_PATH`, is declared in
// `./route-paths`.

/**
 * The web app's page an automation email's unsubscribe link opens, under the recipient's locale,
 * with `?token=`: it writes nothing until its button calls `CONTACT_BOOK_EMAIL_UNSUBSCRIBE_PATH`.
 */
export const CONTACT_BOOK_EMAIL_UNSUBSCRIBE_PAGE_PATH = '/contact-book/unsubscribe';

/** An unsubscribe token: `{org_id}.{suppression_id}.{mac}`, the MAC hex. */
export const contactBookEmailUnsubscribeTokenSchema = z.string().max(200)
  .regex(/^[0-9a-f-]{36}\.[0-9a-f]{64}\.[0-9a-f]{64}$/i);

/**
 * POST /api/contact-book/email-unsubscribe?token=… — no sign-in: the token alone suppresses one
 * mailbox with scope `all` in one org's book. The token rides the query, not a body, because mail
 * clients' one-click unsubscribe (RFC 8058) POSTs to the address in the email's headers with a
 * form body of their own, and the web page's button calls the same address.
 */
export const contactBookEmailUnsubscribeQuery = z.object({ token: contactBookEmailUnsubscribeTokenSchema });
export type ContactBookEmailUnsubscribeQuery = z.input<typeof contactBookEmailUnsubscribeQuery>;

// ---------------------------------------------------------------------------
// The automation reference (D6a)
// ---------------------------------------------------------------------------

/** An automation's `org_list` reference: an org list's name. */
export const orgListReferenceSchema = listName;

// ---------------------------------------------------------------------------
// Responses
// ---------------------------------------------------------------------------

export interface ContactBookContact {
  contact_id: string;
  name: string;
  phone: string | null;
  email: string | null;
  instagram_sid: string | null;
  /** The linked WayAI account (D10), or null. */
  user_id: string | null;
  labels: string[];
  /** The org tags a hub must carry one of to see the contact; empty for every hub. */
  scope_tag_ids: string[];
  environment: ContactEnvironment;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ListContactBookContactsResponse {
  contacts: ContactBookContact[];
  /** Where the next page starts; null on the last page. */
  next_cursor: string | null;
}

/** A member of an org list, as its members page answers it. */
export interface ContactBookListMember extends ContactBookContact {
  /** Pinned to the list (a member the filter does not match is one by its pin only). */
  pinned: boolean;
}

export interface ListContactBookListMembersResponse {
  contacts: ContactBookListMember[];
  next_cursor: string | null;
}

export interface ContactBookContactResponse {
  contact: ContactBookContact;
}

export type ContactImportRowStatus = 'created' | 'duplicate';

export interface ContactImportRowResult {
  /** The row's index in the batch. */
  index: number;
  status: ContactImportRowStatus;
  /** The new contact (`created`), or the one already holding the row's identity (`duplicate`, when it is one of the book's). */
  contact_id: string | null;
}

export interface ImportContactBookContactsResponse {
  results: ContactImportRowResult[];
  created: number;
  duplicates: number;
}

export interface ContactLinkCandidate {
  user_id: string;
  user_name: string | null;
  user_email: string;
  /** The org's hubs where the account holds a hub user. */
  hubs: Array<{ hub_id: string; hub_name: string | null }>;
}

export interface ContactBookLinkCandidatesResponse {
  candidates: ContactLinkCandidate[];
}

export interface ContactBookList {
  list_id: string;
  name: string;
  description: string | null;
  filter: ContactListFilter;
  pin_count: number;
  created_at: string;
  updated_at: string;
}

export interface ListContactBookListsResponse {
  lists: ContactBookList[];
}

export interface ContactBookListResponse {
  list: ContactBookList;
}

/** One list, with how many contacts it holds, every environment and scope alike. */
export interface ContactBookListDetailResponse extends ContactBookListResponse {
  member_count: number;
}

/** An org list as a hub's automation editor picks it: no member data. */
export interface HubOrgListOption {
  name: string;
  description: string | null;
}

export interface ListHubOrgListsResponse {
  lists: HubOrgListOption[];
}

/** A suppression as the book answers it: the kind of identity it is held against, never the identity. */
export interface ContactBookSuppression {
  suppression_id: string;
  kind: SuppressionIdentityKind;
  scope: SuppressionScope;
  /** Who wrote its current scope. */
  source: SuppressionSource;
  created_at: string;
  updated_at: string;
}

export interface ListContactBookSuppressionsResponse {
  suppressions: ContactBookSuppression[];
  /** Where the next page starts; null on the last page. */
  next_cursor: string | null;
}

export interface ContactBookSuppressionResponse {
  suppression: ContactBookSuppression;
}

/** One contact's identities, each with the suppression held against it, or null. */
export interface ContactBookContactSuppressionsResponse {
  identities: Array<{ identity: SuppressionIdentity; suppression: ContactBookSuppression | null }>;
}

/** Why a contact-book write was refused, as `details.reason` carries it. */
export const CONTACT_BOOK_REFUSALS = [
  'contact_identity_taken',
  'contact_identity_required',
  'contact_book_full',
  'contact_list_name_taken',
  'contact_lists_full',
  'contact_list_pins_full',
  'unknown_scope_tags',
  'unknown_contacts',
  'link_not_a_hub_user',
  'link_orgs_full',
] as const;
export type ContactBookRefusal = typeof CONTACT_BOOK_REFUSALS[number];

/** The request bodies, as a client writes them (before the schemas' transforms). */
export type CreateContactBookContactInput = z.input<typeof createContactBookContactBody>;
export type UpdateContactBookContactInput = z.input<typeof updateContactBookContactBody>;
export type ImportContactBookContactsInput = z.input<typeof importContactBookContactsBody>;
export type CreateContactBookListInput = z.input<typeof createContactBookListBody>;
export type UpdateContactBookListInput = z.input<typeof updateContactBookListBody>;
export type UpdateContactBookListPinsInput = z.input<typeof updateContactBookListPinsBody>;
export type CreateContactBookSuppressionInput = z.input<typeof createContactBookSuppressionBody>;
